// WebSocket service for real-time messaging
// Connects to counsellor portal WebSocket server

// Derive WS URL from the API base URL environment variable
const API_BASE = import.meta.env.VITE_COUNSELLOR_API_BASE_URL || '';
const WS_BASE = (() => {
  try {
    const url = new URL(API_BASE);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}/ws/messaging`;
  } catch {
    return 'wss://counsellor.echobuddy.in/ws/messaging';
  }
})();

class MessagingWebSocket {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.messageHandlers = [];
    this.connectionHandlers = [];
    this.conversationId = null;
    this.customerId = null;
  }

  connect(conversationId, customerId) {
    this.conversationId = conversationId;
    this.customerId = customerId;

    try {
      // Close existing connection if any
      if (this.ws) {
        this.ws.close();
      }

      console.log('Connecting WebSocket to:', `${WS_BASE}?conversation_id=${conversationId}&user_id=${customerId}&user_type=customer`);

      // Create WebSocket connection
      this.ws = new WebSocket(`${WS_BASE}?conversation_id=${conversationId}&user_id=${customerId}&user_type=customer`);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.notifyConnectionHandlers('connected');
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);

          // Notify all message handlers
          this.messageHandlers.forEach(handler => handler(data));
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.notifyConnectionHandlers('error');
      };

      this.ws.onclose = () => {
        console.log('WebSocket disconnected');
        this.notifyConnectionHandlers('disconnected');
        this.attemptReconnect();
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.notifyConnectionHandlers('error');
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.conversationId) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

      setTimeout(() => {
        this.connect(this.conversationId, this.customerId);
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.log('Max reconnect attempts reached or no conversation ID');
      this.notifyConnectionHandlers('failed');
    }
  }

  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  sendTyping() {
    this.send({
      type: 'typing',
      conversation_id: this.conversationId,
      sender_type: 'customer',
      sender_id: this.customerId,
    });
  }

  onMessage(handler) {
    this.messageHandlers.push(handler);
    // Return unsubscribe function
    return () => {
      this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
    };
  }

  onConnectionChange(handler) {
    this.connectionHandlers.push(handler);
    // Return unsubscribe function
    return () => {
      this.connectionHandlers = this.connectionHandlers.filter(h => h !== handler);
    };
  }

  clearHandlers() {
    this.messageHandlers = [];
    this.connectionHandlers = [];
  }

  notifyConnectionHandlers(status) {
    this.connectionHandlers.forEach(handler => handler(status));
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.messageHandlers = [];
    this.connectionHandlers = [];
    this.conversationId = null;
    this.customerId = null;
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
export const messagingWebSocket = new MessagingWebSocket();
